"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { useCategories } from "./categories-context"

interface SearchResult {
  id: string
  type: "product" | "category"
  title: string
  subtitle?: string
  image?: string
  category?: string
  price?: string
  url: string
}

interface SearchContextType {
  searchQuery: string
  setSearchQuery: (query: string) => void
  searchResults: SearchResult[]
  isSearching: boolean
  showResults: boolean
  setShowResults: (show: boolean) => void
}

const SearchContext = createContext<SearchContextType | undefined>(undefined)

// Mock product data for search
const mockProducts = [
  {
    id: "1",
    name: "Professional Safety Helmet",
    category: "head-protection",
    price: "€24.99",
    image: "/placeholder.svg?height=64&width=64&text=Helmet",
    sku: "CG-001",
  },
  {
    id: "2",
    name: "Safety Glasses Clear",
    category: "head-protection",
    price: "€12.99",
    image: "/placeholder.svg?height=64&width=64&text=Glasses",
    sku: "CG-002",
  },
  {
    id: "3",
    name: "Work Gloves Heavy Duty",
    category: "hand-protection",
    price: "€18.99",
    image: "/placeholder.svg?height=64&width=64&text=Gloves",
    sku: "CG-003",
  },
  {
    id: "4",
    name: "High-Vis Safety Vest",
    category: "high-visibility",
    price: "€29.99",
    image: "/placeholder.svg?height=64&width=64&text=Vest",
    sku: "CG-004",
  },
  {
    id: "5",
    name: "Steel Toe Boots",
    category: "foot-protection",
    price: "€89.99",
    image: "/placeholder.svg?height=64&width=64&text=Boots",
    sku: "CG-005",
  },
  {
    id: "6",
    name: "Fall Protection Harness",
    category: "fall-protection",
    price: "€134.99",
    image: "/placeholder.svg?height=64&width=64&text=Harness",
    sku: "CG-006",
  },
  {
    id: "7",
    name: "Disposable Gloves Nitrile",
    category: "disposable",
    price: "€15.99",
    image: "/placeholder.svg?height=64&width=64&text=Disposable",
    sku: "CG-007",
  },
  {
    id: "8",
    name: "Welding Helmet Auto-Darkening",
    category: "head-protection",
    price: "€199.99",
    image: "/placeholder.svg?height=64&width=64&text=Welding",
    sku: "CG-008",
  },
  {
    id: "9",
    name: "Cut Resistant Gloves Level 5",
    category: "hand-protection",
    price: "€34.99",
    image: "/placeholder.svg?height=64&width=64&text=Cut+Resistant",
    sku: "CG-009",
  },
  {
    id: "10",
    name: "Reflective Safety Jacket",
    category: "high-visibility",
    price: "€45.99",
    image: "/placeholder.svg?height=64&width=64&text=Reflective",
    sku: "CG-010",
  },
]

export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const { categories } = useCategories()

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)

    // Simulate API delay
    const searchTimeout = setTimeout(() => {
      const query = searchQuery.toLowerCase().trim()
      const results: SearchResult[] = []

      // Search products
      const productResults = mockProducts
        .filter(
          (product) =>
            product.name.toLowerCase().includes(query) ||
            product.sku.toLowerCase().includes(query) ||
            categories
              .find((cat) => cat.id === product.category)
              ?.name.toLowerCase()
              .includes(query),
        )
        .slice(0, 6)
        .map((product) => ({
          id: product.id,
          type: "product" as const,
          title: product.name,
          subtitle: `${product.sku} • ${product.price}`,
          image: product.image,
          category: product.category,
          price: product.price,
          url: `/product/${product.id}`,
        }))

      // Search categories
      const categoryResults = categories
        .filter(
          (category) =>
            category.name.toLowerCase().includes(query) ||
            category.families.some((sub) => sub.toLowerCase().includes(query)),
        )
        .slice(0, 3)
        .map((category) => ({
          id: category.id,
          type: "category" as const,
          title: category.name,
          subtitle: `${category.families.length} families`,
          url: `/catalogue?category=${category.id}`,
        }))

      results.push(...productResults, ...categoryResults)
      setSearchResults(results)
      setIsSearching(false)
    }, 300)

    return () => clearTimeout(searchTimeout)
  }, [searchQuery, categories])

  return (
    <SearchContext.Provider
      value={{
        searchQuery,
        setSearchQuery,
        searchResults,
        isSearching,
        showResults,
        setShowResults,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export function useSearch() {
  const context = useContext(SearchContext)
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider")
  }
  return context
}
